import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, ChevronDown, ChevronUp, Eye, Trash2, User } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useProject } from "@/pages/project/ProjectLayout";
import { ORDER_TYPE_LABELS } from "@/components/panel/CreateOrderPanel";
import { MaterialList } from "@/components/bom/MaterialList";
import { buildMaterialList } from "@/components/bom/materials";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Bill of Materials — compact order list on the left, full preview panel on the right. */
export default function BomPage() {
  const { project, projectId } = useProject();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [defsByName, setDefsByName] = useState(() => new Map());
  const [canManage, setCanManage] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [sectionToggles, setSectionToggles] = useState({});
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: ordersData }, { data: defsData }] = await Promise.all([
        api.get(`/projects/${projectId}/orders`),
        api.get(`/projects/${projectId}/block-definitions`),
      ]);
      setOrders(ordersData.orders || []);
      setCanManage(Boolean(ordersData.can_manage));
      setDefsByName(new Map((defsData.definitions || []).map((d) => [d.name, d])));
    } catch (err) {
      toast.error(apiError(err));
      setOrders([]);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = orders?.find((order) => order.id === selectedId) || null;
  const materials = selected
    ? buildMaterialList(selected.summary, defsByName, selected.order_types)
    : null;

  // Panel Types default open, Selected Panels default closed (both user-toggleable).
  const isSectionOpen = (id, section) =>
    sectionToggles[`${id}:${section}`] ?? (section === "types");
  const toggleSection = (id, section) =>
    setSectionToggles((prev) => ({ ...prev, [`${id}:${section}`]: !isSectionOpen(id, section) }));

  const showInModel = (order) => {
    navigate(`/projects/${projectId}/model?select=1`, {
      state: { uids: order.panel_uids },
    });
  };

  const remove = async (order) => {
    if (confirmId !== order.id) {
      setConfirmId(order.id);
      setTimeout(() => setConfirmId((cur) => (cur === order.id ? null : cur)), 3000);
      return;
    }
    setConfirmId(null);
    setBusyId(order.id);
    try {
      await api.delete(`/projects/${projectId}/orders/${order.id}`);
      toast.success("Order deleted");
      if (selectedId === order.id) setSelectedId(null);
      setOrders((prev) => prev.filter((item) => item.id !== order.id));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main
      data-testid="bom-page"
      className="mx-auto flex w-full max-w-[1500px] flex-1 flex-col overflow-hidden px-4 py-6 sm:px-6"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900 md:text-lg">
            Bill of Materials
          </h2>
          <p className="mono mt-1 text-[10px] uppercase tracking-wider text-slate-400">
            {project?.code || "Project"} · {orders?.length ?? "…"} order(s)
          </p>
        </div>
      </div>

      {orders === null ? (
        <div data-testid="bom-loading" className="py-24 text-center text-sm text-slate-400">
          Loading orders…
        </div>
      ) : orders.length === 0 ? (
        <div
          data-testid="bom-empty-state"
          className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white/70 px-6 py-24 text-center"
        >
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-cyan-50 text-cyan-600">
            <Boxes size={22} />
          </span>
          <p className="text-sm text-slate-600">
            No orders yet. A Procurement user can create one by selecting panels in the Model tab.
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
          <aside
            data-testid="bom-orders-list"
            className="okn-scroll max-h-48 w-full shrink-0 space-y-1.5 overflow-y-auto pr-1 md:max-h-none md:w-64 lg:w-72"
          >
            {orders.map((order) => {
              const active = order.id === selectedId;
              return (
                <button
                  key={order.id}
                  type="button"
                  data-testid={`order-item-${order.id}`}
                  aria-current={active}
                  onClick={() => setSelectedId(order.id)}
                  className={`w-full rounded-xl border px-3.5 py-2.5 text-left transition-colors ${
                    active
                      ? "border-cyan-500 bg-cyan-50/70 shadow-sm"
                      : "border-slate-200 bg-white/90 hover:border-cyan-300"
                  }`}
                >
                  <p
                    data-testid={`order-item-code-${order.id}`}
                    className="font-display truncate text-[13.5px] font-semibold text-slate-900"
                  >
                    {order.code || "Uncoded order"}
                  </p>
                  <p className="mono mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                    {fmtDate(order.created_at)}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {order.order_types.map((key) => (
                      <span
                        key={key}
                        data-testid={`order-item-type-${order.id}-${key}`}
                        className={`rounded-full border px-1.5 py-px text-[9px] font-medium ${
                          key === "complete_panel"
                            ? "border-cyan-300 bg-cyan-50 text-cyan-800"
                            : "border-slate-200 bg-slate-50 text-slate-600"
                        }`}
                      >
                        {ORDER_TYPE_LABELS[key] || key}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </aside>

          <section
            data-testid="order-preview"
            className="okn-scroll min-h-0 min-w-0 flex-1 overflow-y-auto rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm"
          >
            {!selected ? (
              <div
                data-testid="order-preview-empty"
                className="flex h-full min-h-[300px] flex-col items-center justify-center text-center"
              >
                <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-cyan-50 text-cyan-600">
                  <Boxes size={22} />
                </span>
                <p className="text-sm text-slate-500">
                  Select an order from the list to preview its details.
                </p>
              </div>
            ) : (
              <article data-testid={`order-card-${selected.id}`}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="min-w-0">
                    <h3
                      data-testid={`order-code-${selected.id}`}
                      className="font-display truncate text-[17px] font-semibold text-slate-900"
                    >
                      {selected.code || "Uncoded order"}
                    </h3>
                    <p className="mono mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                      {fmtDate(selected.created_at)}
                    </p>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                    {selected.order_types.map((key) => (
                      <span
                        key={key}
                        data-testid={`order-type-badge-${selected.id}-${key}`}
                        className={`rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${
                          key === "complete_panel"
                            ? "border-cyan-300 bg-cyan-50 text-cyan-800"
                            : "border-slate-200 bg-slate-50 text-slate-600"
                        }`}
                      >
                        {ORDER_TYPE_LABELS[key] || key}
                      </span>
                    ))}
                  </div>
                  <p
                    data-testid={`order-creator-${selected.id}`}
                    className="flex shrink-0 items-center gap-1.5 text-[12.5px] font-medium text-slate-700"
                  >
                    <User size={13} className="shrink-0 text-cyan-600" />
                    <span className="truncate">{selected.created_by_name || "Unknown"}</span>
                  </p>
                </div>

                <div className="mt-4 border-t border-slate-100 pt-3">
                  <div className="mb-1.5 flex items-center justify-between">
                    <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      Total Material List
                    </p>
                    <span className="mono rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-500">
                      {selected.panel_count} panel(s)
                    </span>
                  </div>
                  <MaterialList orderId={selected.id} materials={materials} />
                </div>

                <div className="mt-4 grid items-start gap-x-8 gap-y-3 md:grid-cols-2">
                  <div>
                    <button
                      type="button"
                      data-testid={`order-types-toggle-${selected.id}`}
                      onClick={() => toggleSection(selected.id, "types")}
                      aria-expanded={isSectionOpen(selected.id, "types")}
                      className="mono flex w-full items-center gap-1.5 text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-cyan-700"
                    >
                      {isSectionOpen(selected.id, "types") ? (
                        <ChevronUp size={12} />
                      ) : (
                        <ChevronDown size={12} />
                      )}
                      Panel types ({selected.summary.length})
                    </button>
                    {isSectionOpen(selected.id, "types") ? (
                      <div
                        data-testid={`order-summary-${selected.id}`}
                        className="mt-1.5 space-y-1"
                      >
                        {selected.summary.map((row) => (
                          <div
                            key={row.block_definition}
                            className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-[12px]"
                          >
                            <span className="truncate text-slate-700">
                              {row.block_definition}
                            </span>
                            <span className="mono shrink-0 font-medium text-cyan-700">
                              ×{row.count}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <button
                      type="button"
                      data-testid={`order-panels-toggle-${selected.id}`}
                      onClick={() => toggleSection(selected.id, "panels")}
                      aria-expanded={isSectionOpen(selected.id, "panels")}
                      className="mono flex w-full items-center gap-1.5 text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-cyan-700"
                    >
                      {isSectionOpen(selected.id, "panels") ? (
                        <ChevronUp size={12} />
                      ) : (
                        <ChevronDown size={12} />
                      )}
                      Selected panels ({selected.panel_count})
                    </button>
                    {isSectionOpen(selected.id, "panels") ? (
                      <div
                        data-testid={`order-panels-${selected.id}`}
                        className="okn-scroll mt-1.5 max-h-48 space-y-1 overflow-y-auto pr-1"
                      >
                        {(selected.panels || []).map((panel) => (
                          <div
                            key={panel.panel_uid}
                            data-testid={`order-panel-${selected.id}-${panel.panel_uid}`}
                            className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-1.5 text-[11.5px]"
                          >
                            <span className="truncate text-slate-700">{panel.panel_name}</span>
                            <span className="mono shrink-0 text-[10px] uppercase tracking-wider text-slate-400">
                              {panel.block_definition || "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-2 border-t border-slate-100 pt-4">
                  <button
                    data-testid={`order-show-in-model-${selected.id}`}
                    onClick={() => showInModel(selected)}
                    className="mono flex flex-1 items-center justify-center gap-1.5 rounded-full bg-cyan-600 px-3 py-2 text-[10px] uppercase tracking-wider text-white transition-colors hover:bg-cyan-500"
                  >
                    <Eye size={12} /> Show in Model
                  </button>
                  {canManage ? (
                    <button
                      data-testid={
                        confirmId === selected.id
                          ? `order-delete-confirm-${selected.id}`
                          : `order-delete-${selected.id}`
                      }
                      onClick={() => remove(selected)}
                      disabled={busyId === selected.id}
                      className={`mono flex items-center gap-1 rounded-full border px-3 py-2 text-[10px] uppercase tracking-wider transition-colors ${
                        confirmId === selected.id
                          ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                          : "border-slate-200 text-slate-500 hover:border-red-200 hover:text-red-600"
                      }`}
                    >
                      <Trash2 size={12} />
                      {confirmId === selected.id ? "Confirm?" : "Delete"}
                    </button>
                  ) : null}
                </div>
              </article>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
